import { ReactNode, MouseEvent, ElementType } from 'react';
import { clsx } from 'clsx';
import { useNavigate } from 'react-router-dom';
import { GenericKeyValueType } from '../shared/interfaces/generics';
import { Icon } from '../icon/Icon';
import { LinkSize, LinkIconDirection, LinkColor } from './interfaces';
import { getDataIdGenerator } from '../shared/utils/dataId';

export interface LinkProps extends GenericKeyValueType<string, unknown> {
  dataId: string;
  tag?: ElementType;
  icon?: string;
  iconOnly?: boolean;
  iconSrc?: string;
  iconSize?: number;
  iconClasses?: string;
  direction?: LinkIconDirection;
  size?: LinkSize;
  color?: LinkColor;
  onClick?: (e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  children?: ReactNode;
}

export function LinkButton({
  dataId,
  to,
  tag,
  icon,
  iconOnly,
  iconSrc,
  size = 'md',
  direction = 'right',
  iconClasses = '',
  iconSize,
  color = 'primary',
  onClick,
  className,
  ...props
}: LinkProps) {
  const navigate = useNavigate();

  const classNames = clsx('text-link', className, {
    [`thumbnail-${direction}`]: icon || iconSrc,
    'inline-flex': icon || iconSrc,
    [`text-link-${size}`]: !!size,
    [`text-link-${color}`]: !!color,
    'text-link-icon-only': iconOnly
  });

  const dataIdGenerator = getDataIdGenerator(dataId);

  const showIconInLeftDirection = !!(icon && direction === 'left');
  const showIconImageInLeftDirection = !!(iconSrc && direction === 'left');
  const showIconInRightDirection = !!(icon && direction === 'right');
  const showIconImageInRightDirection = !!(iconSrc && direction === 'right');

  const LinkTag = tag || 'a';

  return (
    <LinkTag
      type={tag === 'button' ? 'button' : undefined}
      {...props}
      className={clsx(classNames, tag !== 'button' && 'd-inline-flex align-items-center')}
      data-id={dataIdGenerator(tag === 'button' ? 'button' : 'link')}
      onClick={(e: MouseEvent<HTMLAnchorElement> | MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
          onClick(e);
        }

        if (to) {
          e.preventDefault();
          navigate(to);
        }
      }}
    >
      {showIconInLeftDirection ? (
        <Icon size={iconSize || 1} name={icon as string} className={clsx('thumb', iconClasses)} />
      ) : null}

      {showIconImageInLeftDirection ? <img src={iconSrc} className='thumb' alt='icon' /> : null}

      {props.children}

      {showIconInRightDirection ? (
        <Icon size={iconSize || 1} name={icon as string} className={clsx('thumb', iconClasses)} />
      ) : null}

      {showIconImageInRightDirection ? <img src={iconSrc} className='thumb' alt='icon' /> : null}
    </LinkTag>
  );
}
